import { cn } from '@/lib/utils';

export interface SyncProgressBarProps {
  label: string;
  percent?: number | null;
  detail?: string;
  state?: 'queued' | 'running' | 'failed' | 'completed';
}

export function SyncProgressBar({ label, percent, detail, state = 'running' }: SyncProgressBarProps) {
  const numericPercent = typeof percent === 'number' && Number.isFinite(percent) ? percent : null;
  const resolvedPercent = numericPercent !== null
    ? Math.max(0, Math.min(100, (numericPercent <= 1 ? numericPercent * 100 : numericPercent)))
    : null;
  const displayPercent = resolvedPercent !== null ? Math.round(resolvedPercent) : null;
  const barWidth = resolvedPercent !== null ? `${resolvedPercent}%` : '25%';
  const barClass = cn(
    'h-3.5 rounded-full transition-all duration-500 ease-out',
    state === 'failed' ? 'bg-destructive' : 'bg-primary',
    resolvedPercent === null ? 'animate-pulse' : ''
  );
  const statusText = displayPercent !== null
    ? `${displayPercent}%`
    : state === 'queued'
      ? 'Queued'
      : state === 'failed'
        ? 'Failed'
        : '';

  return (
    <div className="w-full glass rounded-lg mb-1">
      <div className="mx-auto max-w-6xl px-4 py-2 space-y-1">
        <div className="flex items-center justify-between text-xs" role="status" aria-live="polite">
          <span className="text-muted-foreground line-clamp-1">{label}</span>
          {statusText && <span className={cn('font-semibold', state === 'failed' ? 'text-destructive' : 'text-primary')}>{statusText}</span>}
        </div>
        <div className="relative h-3.5 w-full rounded-full bg-secondary overflow-hidden">
          <div className={barClass} style={{ width: barWidth }} aria-hidden="true" />
        </div>
        {detail && <div className="text-[11px] text-muted-foreground text-center" aria-live="polite">{detail}</div>}
      </div>
    </div>
  );
}
